/**
 * Video Showcase
 * --------------
 * An edge-peeking, infinitely looping carousel of click-to-load videos.
 *
 * Why this isn't a `carousel-slider`: Symmetry's slider is finite by design.
 * `setButtonStates()` disables the arrows once the first/last slide is in view
 * and there is no wrap-around, so an infinite loop can't be layered on top of
 * it. This component keeps the theme's `.slider` / `.slider__grid` /
 * `.slider__item` markup and CSS — so it scrolls, snaps and drags like every
 * other slider on the site — but owns its own geometry so the track can loop.
 *
 * How the loop works: the track is filled with `reps * 3` copies of the video
 * set, where `reps` is chosen so that one *block* (`reps` copies) is wider than
 * the viewport. We rest on the middle block. Once scrolling settles, if we've
 * drifted more than half a block from the middle, we jump by exactly one block
 * width. The pixels either side are identical, so the jump is invisible. Only
 * jumping once scrolling has settled is what stops it from cancelling an
 * in-flight smooth scroll.
 *
 * If every video already fits on screen there is nothing to scroll to, so the
 * loop is skipped and the set is centred instead (`is-static`). That decision
 * is re-made on every resize.
 *
 * The peek — how much of a card shows past each edge — is not configured
 * here. The section sizes the cards so that N of them plus a sliver at each
 * edge fill the row (half a card on desktop, a tenth on mobile); this file
 * measures what is actually left over and splits it evenly, which lands on
 * the same numbers and stays symmetrical when a card hits its max width.
 *
 * Videos are never preloaded. A card holds a poster image and nothing else —
 * the video file is fetched when the card is clicked and released again when
 * the lightbox closes.
 */
if (!customElements.get('video-showcase')) {
  /**
   * Delays a call until it stops being made. Local copy so this file has no
   * dependency on main.js beyond the `modal-dialog` element it opens.
   * @param {Function} fn - Callback to run once calls stop.
   * @param {number} wait - Quiet period (in ms) before running.
   * @returns {Function}
   */
  const settled = (fn, wait) => {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  };

  class VideoShowcase extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('.slider');
      this.grid = this.querySelector('.slider__grid');
      this.modal = document.getElementById(this.dataset.modalId);
      this.modalVideo = this.modal ? this.modal.querySelector('.video-showcase-modal__video') : null;

      // Detached after the first build — these are the nodes every rebuild clones from.
      this.templates = [...this.grid.children];
      this.rtl = document.dir === 'rtl';
      this.canLoop = this.dataset.loop === 'true' && this.templates.length > 1;

      this.addListeners();
      this.initReveal();

      if (this.canLoop) {
        this.build();
      } else {
        this.renderStatic();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.clickHandler);
      if (this.modalObserver) this.modalObserver.disconnect();
      if (this.revealObserver) this.revealObserver.disconnect();
      if (!this.canLoop) return;

      this.slider.removeEventListener('scroll', this.settleHandler);
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (!this.dragStartHandler) return;
      this.slider.removeEventListener('mousedown', this.dragStartHandler);
      this.slider.removeEventListener('mousemove', this.dragMoveHandler);
      this.slider.removeEventListener('mouseup', this.dragEndHandler);
      this.slider.removeEventListener('mouseleave', this.dragEndHandler);
      this.slider.removeEventListener('dragstart', this.dragImageHandler);
    }

    addListeners() {
      this.clickHandler = this.handleClick.bind(this);
      this.addEventListener('click', this.clickHandler);

      if (this.modal) {
        // Symmetry's Modal dispatches no close event, so watch its `open` attribute.
        this.modalObserver = new MutationObserver(this.handleModalToggle.bind(this));
        this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      }

      if (!this.canLoop) return;

      this.settleHandler = settled(this.handleSettle.bind(this), 120);
      this.slider.addEventListener('scroll', this.settleHandler);

      this.resizeHandler = this.build.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);

      if (!window.matchMedia('(pointer: fine)').matches) return;

      // Drag-to-scroll, matching the behaviour of the theme's own sliders. It
      // stands in for the arrows the design does without.
      this.canDrag = true;
      this.dragStartHandler = this.handleDragStart.bind(this);
      this.dragMoveHandler = this.handleDragMove.bind(this);
      this.dragEndHandler = this.handleDragEnd.bind(this);
      this.dragImageHandler = (evt) => evt.preventDefault();

      this.slider.addEventListener('mousedown', this.dragStartHandler);
      this.slider.addEventListener('mousemove', this.dragMoveHandler);
      this.slider.addEventListener('mouseup', this.dragEndHandler);
      this.slider.addEventListener('mouseleave', this.dragEndHandler);
      this.slider.addEventListener('dragstart', this.dragImageHandler);
    }

    /**
     * Current scroll distance from the start of the track. Always positive —
     * `scrollLeft` runs negative in right-to-left documents.
     * @returns {number}
     */
    get offset() {
      return Math.abs(this.slider.scrollLeft);
    }

    /**
     * Jumps to an absolute offset without animating, overriding the
     * `scroll-behavior: smooth` the theme sets on `.slider`.
     * @param {number} offset - Distance from the start of the track, in px.
     */
    jumpTo(offset) {
      this.slider.scrollTo({ left: this.rtl ? -offset : offset, behavior: 'instant' });
    }

    /**
     * Measures the layout, then either centres the set (everything fits) or
     * fills the track with enough copies to loop and parks on the middle
     * block. Safe to re-run on resize.
     */
    build() {
      const port = this.slider.clientWidth;
      const probe = this.grid.firstElementChild;
      if (!port || !probe) return;

      const itemWidth = probe.getBoundingClientRect().width;
      if (!itemWidth) return;

      const gap = parseFloat(getComputedStyle(this.grid).columnGap) || 0;
      this.slideSpan = itemWidth + gap;

      // How far in from the edge a snapped card sits: the peeking sliver of
      // the previous card, plus the gap. Rather than re-deriving the section's
      // peek fraction here, split whatever is left over after the whole cards
      // evenly between the two edges. Where the card width is unclamped that
      // reproduces the designed peek exactly — half a card on desktop, a tenth
      // on mobile — and where a card has hit `--vs-card-max-width` on a very
      // wide screen it keeps both edges symmetrical instead of letting the
      // right-hand one run wide.
      const whole = Math.max(1, Math.floor((port - gap) / this.slideSpan));
      const inset = Math.max(0, (port - gap - whole * this.slideSpan) / 2) + gap;
      this.slider.style.scrollPaddingInlineStart = `${inset}px`;

      // Nothing off screen means nothing to scroll to. The 1px allowance
      // covers sub-pixel rounding when the set fills the row exactly.
      const setWidth = this.templates.length * this.slideSpan - gap;
      if (setWidth <= port + 1) {
        this.renderStatic();
        return;
      }

      this.classList.remove('is-static');
      if (this.canDrag) this.slider.classList.add('is-grabbable');

      // A block has to out-measure the viewport, so no flick can reach the
      // real end of the track before the next settle re-centres us.
      const setSpan = this.templates.length * this.slideSpan;
      const reps = Math.min(12, Math.max(1, Math.ceil((port * 1.5) / setSpan)));

      if (reps !== this.reps) {
        this.reps = reps;
        const track = document.createDocumentFragment();
        for (let block = 0; block < reps * 3; block += 1) {
          this.templates.forEach((item) => track.appendChild(item.cloneNode(true)));
        }

        this.grid.replaceChildren(track);
        this.setRevealOrder();
      }

      // Measured off the DOM rather than multiplied out, so that repeated jumps
      // can't accumulate sub-pixel drift.
      const middle = this.grid.children[reps * this.templates.length];
      this.blockWidth = Math.abs(middle.offsetLeft - this.grid.children[0].offsetLeft);

      // Rest on a real snap point, or mandatory snapping would pull us off it
      // and the peek would be lost.
      this.restOffset = this.blockWidth - inset;
      this.jumpTo(this.restOffset);
    }

    /**
     * Drops back to one un-cloned, centred set: used for a lone video and
     * whenever the whole set already fits across the row.
     */
    renderStatic() {
      if (this.reps !== 0) {
        this.reps = 0;
        this.grid.replaceChildren(...this.templates.map((item) => item.cloneNode(true)));
        this.setRevealOrder();
      }

      // Zeroing this is what makes `handleSettle` a no-op while static.
      this.blockWidth = 0;
      this.slider.classList.remove('is-grabbable');
      this.classList.add('is-static');
    }

    /**
     * Numbers the cards so the entrance animation staggers left to right,
     * starting from the card peeking in at the left edge.
     */
    setRevealOrder() {
      const first = this.reps ? this.reps * this.templates.length - 1 : 0;
      [...this.grid.children].forEach((item, index) => {
        item.style.setProperty('--vs-reveal-order', Math.max(0, index - first));
      });
    }

    /**
     * Re-centres the track once scrolling has stopped. The blocks either side
     * of the middle are pixel-identical to it, so the jump can't be seen.
     */
    handleSettle() {
      if (this.dragging || !this.blockWidth) return;

      // Jumping by exactly one block keeps the position within the repeating
      // pattern — and so the snap alignment — untouched.
      const { offset } = this;
      const drift = offset - this.restOffset;
      if (drift < this.blockWidth * -0.5) {
        this.jumpTo(offset + this.blockWidth);
      } else if (drift > this.blockWidth * 0.5) {
        this.jumpTo(offset - this.blockWidth);
      }
    }

    /**
     * Fades the cards up in sequence once 5% of the section is on screen.
     * Reveals immediately where IntersectionObserver is missing, so the cards
     * can never be left stranded invisible.
     */
    initReveal() {
      if (!('IntersectionObserver' in window)) {
        this.classList.add('is-revealed');
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.classList.add('is-revealed');
          observer.disconnect();
        },
        { threshold: 0.05 }
      );

      this.revealObserver.observe(this);
    }

    /**
     * Opens a card's video. The card is one big button, so a click anywhere on
     * it that wasn't the end of a drag counts.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {
      const playBtn = evt.target.closest('.video-showcase__play-btn');
      if (playBtn && !this.dragged) this.openVideo(playBtn);
    }

    /**
     * Loads and plays a video in the lightbox. This is the only place a video
     * file is ever requested.
     * @param {HTMLButtonElement} btn - Play button that was pressed.
     */
    openVideo(btn) {
      if (!this.modalVideo) return;

      // Modal.open() calls pauseAllMedia(), so set the source afterwards.
      this.modal.open(btn);
      this.modalVideo.poster = btn.dataset.videoPoster || '';
      this.modalVideo.src = btn.dataset.videoSrc;
      this.modalVideo.play().catch(() => {
        /* controls are showing, so the visitor can start it themselves */
      });
    }

    /** Releases the lightbox video on close so it stops buffering. */
    handleModalToggle() {
      if (this.modal.hasAttribute('open') || !this.modalVideo) return;

      this.modalVideo.pause();
      this.modalVideo.removeAttribute('src');
      this.modalVideo.load();
    }

    /**
     * Handles 'mousedown' events on the slider.
     * @param {object} evt - Event object.
     */
    handleDragStart(evt) {
      if (!this.blockWidth) return;

      this.dragging = true;
      this.dragged = false;
      this.dragOriginX = evt.pageX;
      this.dragOriginOffset = this.slider.scrollLeft;
      this.slider.classList.add('is-grabbing');
    }

    /**
     * Handles 'mousemove' events on the slider.
     * @param {object} evt - Event object.
     */
    handleDragMove(evt) {
      if (!this.dragging) return;
      evt.preventDefault();

      this.dragged = true;
      this.slider.classList.add('is-dragging');
      this.slider.scrollTo({
        left: this.dragOriginOffset - (evt.pageX - this.dragOriginX),
        behavior: 'instant'
      });
    }

    /** Handles 'mouseup' and 'mouseleave' events on the slider. */
    handleDragEnd() {
      if (!this.dragging) return;

      this.dragging = false;
      this.slider.classList.remove('is-grabbing', 'is-dragging');

      // Swallow the click that ends a drag, then re-arm the play buttons.
      if (this.dragged) {
        setTimeout(() => {
          this.dragged = false;
        }, 0);
      }
    }
  }

  customElements.define('video-showcase', VideoShowcase);
}
